import { SplashScreen } from '@capacitor/splash-screen';
import { IonicConfig, createAnimation } from '@ionic/core';
import {
  IonApp,
  IonRouterOutlet,
  IonSpinner,
  IonSplitPane,
  isPlatform,
  setupIonicReact,
} from '@ionic/react';
import { IonReactRouter } from '@ionic/react-router';
import '@ionic/react/css/core.css';
import '@ionic/react/css/display.css';
import '@ionic/react/css/flex-utils.css';
import '@ionic/react/css/float-elements.css';
import '@ionic/react/css/normalize.css';
import '@ionic/react/css/padding.css';
import '@ionic/react/css/palettes/dark.class.css';
import '@ionic/react/css/structure.css';
import '@ionic/react/css/text-alignment.css';
import '@ionic/react/css/text-transformation.css';
import '@ionic/react/css/typography.css';
import { ThemeProvider, createTheme } from '@mui/material';
import * as Sentry from '@sentry/react';
import { History, createBrowserHistory, createMemoryHistory } from 'history';
import { ReactElement, useCallback, useEffect, useMemo, useRef } from 'react';
import { Redirect, Route, Switch, useHistory, useParams } from 'react-router';
import { CloudApiProvider } from './api/api.provider';
import { AuthorizationProvider } from './api/authorization.provider';
import {
  useSubscribeToPlanDetailsSubscription,
  useSubscribeToTenantSubscription,
} from './api/graphql/generated';
import { useAppUpdateCheck } from './api/hooks/app-hooks';
import { useAppState } from './api/hooks/app-state.hook';
import { useRegisterPushNotification } from './api/hooks/push-notification.hooks';
import { useLogoutIfTenantDeactivated } from './api/hooks/tenant.hooks';
import { WifiProvider } from './api/hooks/wifi.hooks';
import { useCurrentUserProp } from './api/user.hooks';
import { UserInfo, UserProvider } from './api/user.provider';
import { AppMode, AppModeProvider } from './components/app/app-mode-provider';
import {
  AppIconContextProvider,
  useGetBranding,
  useGetPartner,
} from './components/branding/branding.hooks';
import { iosPopoverTopEnterAnimation } from './components/button/animation/popover.ios.enter';
import { mdPopoverTopEnterAnimation } from './components/button/animation/popover.md.enter';
import { HardwareBackButtonListener } from './components/button/back-button.listener';
import { ErrorBoundaryFallback } from './components/error/error-boundary-fallback';
import { AppUrlListener } from './components/listener/AppUrlListener';
import { PushNotificationListener } from './components/listener/PushNotificationListener';
import { Menu } from './components/menu/menu';
import { PWANotificationBadge } from './components/pwa/pwa-notification-badge';
import { IntercomSupportContextProvider } from './components/support/support.context';
import {
  configureAnalytics,
  setAnalyticsUserContext,
} from './config/analytics';
import { ApplicationConfig } from './config/config';
import { configureSentry, setSentryUserContext } from './config/sentry';
import { DirectConnectRoutes } from './direct-connect/route/direct-connect-route';
import { useSyncLanguage } from './i18n/i18n.hooks';
import { KioskType, useIsKioskModeEnabled } from './kiosk/kiosk';
import { LoginConfirmPage } from './pages/auth/login-confirm.page';
import { LoginPasswordConfirmPage } from './pages/auth/login-password-confirm.page';
import { LoginPage } from './pages/auth/login.page';
import { LogoutPage } from './pages/auth/logout.page';
import { SignupPage } from './pages/auth/signup.page';
import Home from './pages/home/home.page';
import { NotificationsPage } from './pages/notifications/notifications.page';
import { Page } from './pages/page';
import { RolePage } from './pages/role/role.page';
import {
  AdminProtectedRoute,
  BillingManagerProtectedRoute,
  ProtectedRoute,
  RedirectIfAuthenticated,
  TenantRouteParams,
} from './pages/routes';
import { SubscriptionPage } from './pages/subscription/subscription.page';
import { TeamListPage } from './pages/team/team-list.page';
import { TeamPage } from './pages/team/team.page';
import { Adminland } from './pages/tenant/adminland.page';
import { TenantEventIntegrationsPage } from './pages/tenant/tenant-event-integrations.page';
import { TenantExportPage } from './pages/tenant/tenant-export.page';
import { TenantMachinesPage } from './pages/tenant/tenant-machines.page';
import { TenantRolesPage } from './pages/tenant/tenant-roles.page';
import { TenantTeamsPage } from './pages/tenant/tenant-teams.page';
import { TenantUsersPage } from './pages/tenant/tenant-users.page';
import { TenantAdminPage } from './pages/tenant/tenant.page';
import { UserPage } from './pages/user/user.page';
import {
  BeaconRouterContextProvider,
  useBeaconRouter,
} from './route/route.hooks';
import { Route as BeaconRoute } from './route/route.types';
import { usePersistNavigationHistory } from './store/nav-history/nav-history.hooks';
import {
  useActiveWorkspace,
  useHydrateAppLazy,
  useSwitchWorkspaceLazy,
} from './store/workspace/workspace.hooks';
import './theme/custom.css';
import {
  useDarkMode,
  useSyncTheme,
  useSyncThemeWithoutPreference,
} from './theme/dark-mode.hook';
import './theme/esab.css';
import {
  useKeyboardDidHide,
  useKeyboardWillShow,
  useThemedKeyboard,
} from './theme/keyboard.hook';
import './theme/variables.css';
import { useSafeArea } from './theme/safe-area.hook';
import { sleep } from './util/async';
import { logger } from './util/logger';
import { useLockMobileOrientation } from './util/screen-orientation';
import { setTextZoom } from './util/text-utils';

let history: History<unknown> = createBrowserHistory({
  basename: ApplicationConfig.public.url,
});

// if we are in kiosk mode and running on a teach pendant then disable
// browser history since it can conflict with the teach pendants navigation
if (
  ApplicationConfig.kiosk.enabled &&
  ApplicationConfig.kiosk.type === KioskType.TEACH_PENDANT
) {
  history = createMemoryHistory();
}

const getConfig = (): Partial<IonicConfig> => {
  const commonConfig: Partial<IonicConfig> = {
    popoverEnter: mdPopoverTopEnterAnimation,
  };

  // place to override config on a platform level
  if (isPlatform('ios')) {
    return {
      ...commonConfig,
      menuType: 'push',
      swipeBackEnabled: false,
      popoverEnter: iosPopoverTopEnterAnimation,
    };
  } else if (isPlatform('desktop')) {
    return {
      ...commonConfig,
      // disable nav animations for desktop
      navAnimation: () => {
        return createAnimation();
      },
    };
  }

  return { ...commonConfig };
};

setupIonicReact(getConfig());

const APP_ROOT_CLASS = 'app-root';

const App = (): ReactElement => {
  useLockMobileOrientation();
  useThemedKeyboard();
  useSyncThemeWithoutPreference();
  useSafeArea();

  useEffect(() => {
    // This is necessary since configureAnalytics injects a <script> into the
    // html. When offline, the script injection is causing the Webview to hang
    // until it timesout.
    if (ApplicationConfig.env !== 'e2e') {
      configureSentry();
      configureAnalytics();
    }
  }, []);

  const hideSplash = useCallback(async () => {
    try {
      await sleep(500);
      SplashScreen.hide({ fadeOutDuration: 100 });
    } catch (_error) {
      // .. do nothing if this is unavilable
    }
  }, []);

  useAppState({
    foreground: () => logger.info('app entering foreground'),
    background: () => logger.info('app entering background'),
  });

  useAppState({ foreground: setTextZoom });

  useEffect(() => {
    hideSplash();
  }, [hideSplash]);

  return (
    <AppIconContextProvider>
      <Sentry.ErrorBoundary fallback={ErrorBoundaryFallback}>
        <BeaconRouterContextProvider>
          <AppContent />
        </BeaconRouterContextProvider>
      </Sentry.ErrorBoundary>
    </AppIconContextProvider>
  );
};

const AppContent = (): ReactElement | null => {
  const classList: string[] = [APP_ROOT_CLASS];

  const getIonAppElement = useCallback(() => {
    const targets = document.getElementsByClassName(APP_ROOT_CLASS);
    const el = targets[0];
    return el;
  }, []);

  useKeyboardWillShow(() => {
    const el = getIonAppElement();
    el?.classList.add('keyboard-showing');
  });

  useKeyboardDidHide(() => {
    const el = getIonAppElement();
    el?.classList.remove('keyboard-showing');
  });

  const branding = useGetBranding();

  if (branding?.cssClass) {
    classList.push(branding?.cssClass);
  }

  const isDarkMode = useDarkMode();

  const muiTheme = useMemo(() => {
    return createTheme({
      palette: {
        mode: isDarkMode ? 'dark' : 'light',
      },
    });
  }, [isDarkMode]);

  return (
    <IonApp className={classList.join(' ')}>
      <ThemeProvider theme={muiTheme}>
        <WifiProvider>
          <IonReactRouter history={history}>
            <HardwareBackButtonListener
              priority={10}
              disableBackButton={true}
            />
            <AppUrlListener />
            <AppRoutes />
          </IonReactRouter>
        </WifiProvider>
      </ThemeProvider>
    </IonApp>
  );
};

const AppRoutes = () => {
  const isKioskModeEnabled = useIsKioskModeEnabled();

  return (
    <Switch>
      <Page path={`${BeaconRoute.DIRECT_CONNECT}/*`}>
        <DirectConnectRoutes />
      </Page>
      <Page path="*">
        {isKioskModeEnabled ? <KioskRoutes /> : <CloudRoutes />}
      </Page>
    </Switch>
  );
};

const KioskRoutes = () => {
  const { builder } = useBeaconRouter();
  return <Redirect to={builder.getDirectConnectKioskRoute()} />;
};

const CloudRoutes = () => {
  const { builder } = useBeaconRouter();

  const activeWorkspace = useActiveWorkspace();

  const userInfo = useMemo<UserInfo | undefined>(() => {
    if (activeWorkspace?.tenant?.id && activeWorkspace?.user?.id) {
      return {
        tenantId: activeWorkspace.tenant.id,
        tenantName: activeWorkspace.tenant.name ?? undefined,
        userId: activeWorkspace.user.id,
      };
    }
  }, [activeWorkspace]);

  // we wrap all cloud routes with the cloud api provider. we use a nested
  // switch here to prevent a redirect loop since the <ProtectedRoute />
  // component needs to be a direct child of the Switch in order to properly
  // use the computedMatch prop that is provided by react router

  return (
    <AppModeProvider mode={AppMode.CLOUD}>
      <CloudApiProvider apollo={{ uri: ApplicationConfig.graphql.uri }}>
        <UserProvider userInfo={userInfo}>
          <Switch>
            <Page
              path={builder.getAuthLoginRoute()}
              component={LoginPage}
              exact
              i18nTitleKey={'common:page_login'}
            />
            <Page
              path={builder.getAuthSignupRoute()}
              component={SignupPage}
              exact
              i18nTitleKey={'common:page_signup'}
            />
            <Page
              path={builder.getAuthLogoutRoute()}
              component={LogoutPage}
              exact
              i18nTitleKey={'common:page_logout'}
            />
            <Page
              path={builder.getAuthConfirmRoute()}
              component={LoginConfirmPage}
              exact
              i18nTitleKey={'common:page_confirm_login'}
            />
            <Page
              path={builder.getAuthConfirmPasswordRoute()}
              component={LoginPasswordConfirmPage}
              exact
              i18nTitleKey={'common:page_confirm_login'}
            />
            <Page exact path="/" component={RedirectIfAuthenticated} />
            <ProtectedRoute path="/:tenantId?/*">
              <CloudContent />
            </ProtectedRoute>
            <Page path="*">
              <Redirect to={builder.getAuthLoginRoute()} />
            </Page>
          </Switch>
        </UserProvider>
      </CloudApiProvider>
    </AppModeProvider>
  );
};

const CloudContent = (): ReactElement | null => {
  const { builder } = useBeaconRouter();
  const { replace } = useHistory();
  const hydrateApp = useHydrateAppLazy();

  useAppState({
    foreground: () => {
      hydrateApp();
    },
  });

  const { tenantId } = useParams<TenantRouteParams>();
  const activeWorkspace = useActiveWorkspace();

  const switchWorkspace = useSwitchWorkspaceLazy();
  const email = useCurrentUserProp<string>('email');
  const [registerPushNotifications] = useRegisterPushNotification();

  const activeTenantId = activeWorkspace?.tenant.id;
  const userId = activeWorkspace?.user.id;

  const routerRef = useRef<HTMLIonRouterOutletElement | null>(null);

  // subscribe to tenant so any tenant changes will be automatically
  // reflected in the cache
  useSubscribeToTenantSubscription();
  useSubscribeToPlanDetailsSubscription();
  useAppUpdateCheck();
  usePersistNavigationHistory(tenantId);
  useSyncTheme();
  useSyncLanguage();
  useLogoutIfTenantDeactivated();

  // required to run in main so that we can sync branding changes
  // if user theme is different than the saved tenant theme
  useGetPartner();

  useEffect(() => {
    if (activeTenantId === tenantId) {
      if (email) {
        setSentryUserContext(email);
        if (userId && tenantId) {
          setAnalyticsUserContext(activeTenantId, userId, email);
          registerPushNotifications({
            skipRegisterIfAlreadyGranted: false,
            hideDeniedPrompt: true,
            hideRequestPrompt: false,
          });
        }
      }
    }
  }, [email, activeTenantId, userId, tenantId, registerPushNotifications]);

  useEffect(() => {
    if (tenantId) {
      switchWorkspace(tenantId);
    } else {
      replace(builder.getAuthLoginRoute());
    }
  }, [replace, tenantId, switchWorkspace, builder]);

  // don't render until workspace has successfully switched to avoid making api calls
  // with the old tenant id
  if (activeWorkspace?.tenant.id !== tenantId) {
    return (
      <div className="full-height-width content-center">
        <IonSpinner className="ion-align-self-center" />
      </div>
    );
  }

  return (
    <IntercomSupportContextProvider>
      <AuthorizationProvider>
        <IonSplitPane contentId="main">
          <PushNotificationListener />
          <PWANotificationBadge />
          <Menu />
          <IonRouterOutlet id="main" ref={routerRef}>
            <Page
              exact
              path={builder.getHomeRoute()}
              component={Home}
              i18nTitleKey={'common:page_home'}
            />
            <AdminProtectedRoute
              exact
              path={builder.getAdminRoute()}
              i18nTitleKey={'common:page_adminland'}
            >
              <Adminland />
            </AdminProtectedRoute>
            <AdminProtectedRoute
              exact
              path={builder.getAdminTenantRoute()}
              i18nTitleKey={'common:page_adminland'}
            >
              <TenantAdminPage />
            </AdminProtectedRoute>
            <AdminProtectedRoute
              exact
              path={builder.getAdminUsersRoute()}
              i18nTitleKey={'common:page_adminland'}
            >
              <TenantUsersPage />
            </AdminProtectedRoute>
            <AdminProtectedRoute
              exact
              path={builder.getAdminRolesRoute()}
              i18nTitleKey={'common:page_adminland'}
            >
              <TenantRolesPage />
            </AdminProtectedRoute>
            <AdminProtectedRoute
              exact
              path={builder.getAdminTeamsRoute()}
              i18nTitleKey={'common:page_adminland'}
            >
              <TenantTeamsPage />
            </AdminProtectedRoute>
            <AdminProtectedRoute
              exact
              path={builder.getAdminMachinesRoute()}
              i18nTitleKey={'common:page_adminland'}
            >
              <TenantMachinesPage />
            </AdminProtectedRoute>
            <AdminProtectedRoute
              exact
              path={builder.getAdminEventIntegrationsRoute()}
              i18nTitleKey={'common:page_adminland'}
            >
              <TenantEventIntegrationsPage />
            </AdminProtectedRoute>
            <AdminProtectedRoute
              exact
              path={builder.getAdminUserRoute(':id')}
              i18nTitleKey={'common:page_adminland'}
            >
              <UserPage defaultBackRoute={builder.getAdminUsersRoute()} />
            </AdminProtectedRoute>
            <AdminProtectedRoute
              path={builder.getAdminRoleRoute(':id')}
              i18nTitleKey={'common:page_adminland'}
            >
              <RolePage defaultBackRoute={builder.getAdminRolesRoute()} />
            </AdminProtectedRoute>
            <AdminProtectedRoute
              exact
              path={builder.getAdminExportRoute()}
              i18nTitleKey={'common:page_adminland'}
            >
              <TenantExportPage />
            </AdminProtectedRoute>
            <BillingManagerProtectedRoute
              exact
              path={builder.getBillingRoute()}
              i18nTitleKey={'common:page_billing'}
            >
              <SubscriptionPage />
            </BillingManagerProtectedRoute>
            <Page
              exact
              path={builder.getNotificationsRoute()}
              component={NotificationsPage}
              i18nTitleKey={'common:page_my_notifications'}
            />
            <Page
              exact
              path={builder.getTeamsRoute()}
              component={TeamListPage}
              i18nTitleKey={'common:page_teams'}
            />
            <Route path={builder.getTeamRoute(':id')} component={TeamPage} />
            <Page
              exact
              path={builder.getUserRoute(':id')}
              component={UserPage}
              i18nTitleKey={'common:page_my_profile'}
            />
            {/* TODO: Fallback routes currently mess up routing for team pages */}
          </IonRouterOutlet>
        </IonSplitPane>
      </AuthorizationProvider>
    </IntercomSupportContextProvider>
  );
};

export default App;
